import * as Sentry from '@sentry/react';
import { decamelizeKeys, decamelize } from 'humps';
import { setUserId } from '@amplitude/analytics-browser';
import { i18n, moment } from '@international/mastodon-i18n';
import resources from '../lib/translations/resources';
import { request, initializeDDRUM } from './helper';
import { normalizeHealthScreenings } from '../lib/helpers';
import { IS_PRODUCTION, AUTH_METHODS, CHECKR_BFF_API_BASE } from '../constants';
import { slashDatePattern } from '../lib/dateFormat';
import { sha256 } from '../lib/sha256';
import { trackAnalyticsEvent } from './analytics';
import { ANALYTICS_EVENTS, ANALYTICS_PROPERTIES } from '../lib/analytics';

/*
 * Action Types
 */

export const FETCH_REPORTS_UNKNOWN_FAILURE = 'FETCH_REPORTS_UNKNOWN_FAILURE';
export const FETCH_REPORTS_FAILURE = 'FETCH_REPORTS_FAILURE';
export const FETCH_REPORTS_FAILURE_INTL = 'FETCH_REPORTS_FAILURE_INTL';
export const FETCH_REPORTS_BUSY = 'FETCH_REPORTS_BUSY';
export const CLEAR_FETCH_REPORTS_FAILURE = 'CLEAR_FETCH_REPORTS_FAILURE';
export const FETCH_REPORTS_REDIRECT = 'FETCH_REPORTS_REDIRECT';
export const FETCH_REPORTS_REQUEST = 'FETCH_REPORTS_REQUEST';
export const FETCH_REPORTS_SUCCESS = 'FETCH_REPORTS_SUCCESS';
export {
  LOGOUT_CANDIDATE,
  logoutCandidate,
  logoutCandidateComplete,
  teardownUnifiedLoginSession,
} from './logoutCandidate';
export const CLEAR_LOGGED_OUT = 'CLEAR_LOGGED_OUT';
export const RELOAD_REPORT = 'RELOAD_REPORT';
export const NON_CANDIDATE_LOGGED_IN = 'NON_CANDIDATE_LOGGED_IN';
export const PORTAL_RESET = 'PORTAL_RESET';

/*
 * Action Type Helpers
 */

export const fetchReportsRequest = search => ({
  type: FETCH_REPORTS_REQUEST,
  search,
});

export const fetchReportsSuccess = ({
  data: reports,

  meta: { token, applicantId, notices, termsOfService },
  privacyCandidateIds,
  privacyCandidateName,
  count,
}) => ({
  applicantId,
  privacyCandidateIds,
  privacyCandidateName,
  count,
  notices,
  reports,
  termsOfService,
  token,
  type: FETCH_REPORTS_SUCCESS,
});

export const fetchCandidateSuccess = ({
  data: reports,
  meta: {
    token,
    firstName,
    lastName,
    applicantId,
    termsOfService,
    country,
    email,
  },
  count,
}) => ({
  applicantId,
  firstName,
  count,
  reports,
  termsOfService,
  token,
  country,
  email,
  privacyCandidateIds: [
    ...new Set(reports.map(({ candidateId }) => candidateId)),
  ],
  // Only join the name parts we have, so a missing part never becomes the
  // literal string "null". Left undefined when both are missing so the privacy
  // reducer skips persisting it and DeleteData falls back to the email.
  privacyCandidateName:
    [firstName, lastName].filter(Boolean).join(' ') || undefined,
  type: FETCH_REPORTS_SUCCESS,
});

export const fetchReportsFailure = (isInternational = false) => ({
  type: isInternational ? FETCH_REPORTS_FAILURE_INTL : FETCH_REPORTS_FAILURE,
});

export const fetchReportsBusy = () => ({
  type: FETCH_REPORTS_BUSY,
});

// Portal-only reset, owned by the report slice. Auth flows dispatch it
// explicitly instead of the portal reducer reacting to auth action types, which
// stops firing once auth state leaves Redux (ECDEX-3441). Unlike
// clearFetchReportsFailure, it does not touch candidateLogin.
//
// A new login session must not inherit reports or errors from a previous SSN
// or WorkOS attempt.
export const resetPortal = () => ({ type: PORTAL_RESET });

// Restoring a candidate session on top of a failed report fetch leaves Auth and
// Login disagreeing, so they bounce. Clear the failed portal; leave one that
// already loaded reports alone.
export const resetFailedPortal = () => (dispatch, getState) => {
  const portal = getState()?.portal;
  if (portal?.error && !portal.reportsLoaded) {
    dispatch(resetPortal());
  }
};

export const clearFetchReportsFailure = () => ({
  type: CLEAR_FETCH_REPORTS_FAILURE,
});

export const fetchReportsUnknownFailure = () => ({
  type: FETCH_REPORTS_UNKNOWN_FAILURE,
});

export const fetchReportsRedirect = () => ({
  type: FETCH_REPORTS_REDIRECT,
});

/*
 * Action Helpers
 */

const formatDob = ({ month, day, year }) =>
  moment(`${month}/${day}/${year}`, 'MM/DD/YYYY').format();

export const formatSearch = (values, international) => {
  const search = { ...values };

  if (search.dob) {
    if (international) {
      search.dob = moment(search.dob, slashDatePattern()).format();
    } else {
      search.dob = formatDob(search.dob);
    }
  }

  if (search.idType && international) {
    search.idType = decamelize(search.idType);
  }

  return search;
};

const initializeIntegrations = data => {
  if (data === null || !data.accountId) return;

  const { email, applicantId } = data;
  initializeDDRUM(email, applicantId);
};

/*
 * Actions
 */

const setCandidateLocale = ({ country, locale }) => {
  if (locale && country) {
    i18n.setLocale({
      value: locale,
      resources: resources(),
      country,
      useCookie: true,
    });
  }
};

const SERVER_ERROR_STATUSES = [500, 502, 503, 504];

// A fetch that never reached our servers (offline, blocked, aborted, a dropped
// mobile connection) rejects with a bare TypeError whose message depends on the
// browser. The candidate still sees the unknown-failure message; there is
// nothing for us to fix, so it is not reported to Sentry. Match the messages
// rather than every TypeError so a real bug in the success path still reports.
const NETWORK_ERROR_MESSAGES = new Set([
  'Failed to fetch',
  'Load failed',
  'NetworkError when attempting to fetch resource.',
]);

const isNetworkError = error =>
  error instanceof TypeError && NETWORK_ERROR_MESSAGES.has(error.message);

const classifyErrorType = status => {
  if (typeof status === 'undefined') return 'unknown';
  if (SERVER_ERROR_STATUSES.includes(status)) return 'server_error';
  return 'auth_failure';
};

const candidateFetch = (dispatch, isInternational) => {
  const success = json => {
    json.data.forEach(normalizeHealthScreenings);
    if (IS_PRODUCTION) {
      const accountId = json.data?.[0]?.candidateId;
      const { firstName, lastName, applicantId, email } = json.meta;
      initializeIntegrations({
        firstName,
        lastName,
        applicantId,
        email,
        accountId,
      });
    }
    if (isInternational) {
      setCandidateLocale(json.meta);
    }
    dispatch(fetchCandidateSuccess({ ...json }));
  };
  return {
    onSuccess: async json => {
      try {
        if (json.meta?.email) {
          setUserId(await sha256(json.meta.email));
        }
      } catch {
        // Analytics must never break the login flow
      }
      success(json);
    },
    onError: error => {
      const errorType = classifyErrorType(error.status);
      if (errorType === 'unknown') {
        dispatch(fetchReportsUnknownFailure());
      } else if (errorType === 'server_error') {
        dispatch(fetchReportsBusy());
      } else {
        dispatch(fetchReportsFailure(isInternational));
      }

      const approvedErrors = [401, 404, ...SERVER_ERROR_STATUSES];
      if (!approvedErrors.includes(error.status) && !isNetworkError(error)) {
        Sentry.captureException(error);
      }
    },
  };
};

const fetchCandidate = (values, isInternational, dispatch) => {
  const search = formatSearch(values, isInternational);

  dispatch(fetchReportsRequest(search));

  const submittedEvent = isInternational
    ? ANALYTICS_EVENTS.CANDIDATE_PORTAL_INTERNATIONAL_LOGIN_SUBMITTED
    : ANALYTICS_EVENTS.CANDIDATE_PORTAL_SSN_LOGIN_SUBMITTED;
  const successEvent = isInternational
    ? ANALYTICS_EVENTS.CANDIDATE_PORTAL_INTERNATIONAL_LOGIN_SUCCESS
    : ANALYTICS_EVENTS.CANDIDATE_PORTAL_SSN_LOGIN_SUCCESS;
  const failedEvent = isInternational
    ? ANALYTICS_EVENTS.CANDIDATE_PORTAL_INTERNATIONAL_LOGIN_FAILED
    : ANALYTICS_EVENTS.CANDIDATE_PORTAL_SSN_LOGIN_FAILED;

  dispatch(trackAnalyticsEvent(submittedEvent));

  const options = {
    method: 'POST',
    body: JSON.stringify(decamelizeKeys(search)),
    // We do not want to retry on fetchReports, since it uses a RECAPTCHA key that may have been consumed
    retries: 0,
  };

  const { onSuccess, onError } = candidateFetch(dispatch, isInternational);
  return request(`${CHECKR_BFF_API_BASE}/reports/search/candidate`, options)
    .then(async json => {
      await onSuccess(json);
      const successProps = {
        [ANALYTICS_PROPERTIES.AUTH_METHOD]: isInternational
          ? AUTH_METHODS.INTERNATIONAL
          : AUTH_METHODS.SSN,
      };
      dispatch(trackAnalyticsEvent(successEvent, successProps));
      dispatch(
        trackAnalyticsEvent(ANALYTICS_EVENTS.CANDIDATE_PORTAL_LOGIN_SUCCESS, {
          ...successProps,
          [ANALYTICS_PROPERTIES.IS_CANDIDATE]: true,
        }),
      );
    })
    .catch(error => {
      onError(error);
      dispatch(
        trackAnalyticsEvent(failedEvent, {
          'Error Type': classifyErrorType(error.status),
          'Error Status': error.status,
        }),
      );
    });
};

const fetchCandidateWithIds = (values, dispatch) => {
  const { candidateIds, candidateJwt } = values;
  dispatch(fetchReportsRequest({ candidateIds }));

  const options = {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${candidateJwt}`,
    },
    body: JSON.stringify(decamelizeKeys({ candidateIds })),
    retries: 3,
  };

  const { onSuccess, onError } = candidateFetch(dispatch);
  return request(`${CHECKR_BFF_API_BASE}/reports/candidates/auth`, options)
    .then()
    .then(onSuccess)
    .catch(onError);
};

export const fetchCandidateReports = values => dispatch => {
  return fetchCandidate(values, false, dispatch);
};

export const fetchCandidateReportsIntl = values => dispatch => {
  return fetchCandidate(values, true, dispatch);
};

export const fetchCandidateUsingIds = values => dispatch => {
  return fetchCandidateWithIds(values, dispatch);
};

export const clearLoggedOut = () => ({
  type: CLEAR_LOGGED_OUT,
});

export const reloadReport = () => ({
  type: RELOAD_REPORT,
});

export const nonCandidateLoggedIn = () => ({
  type: NON_CANDIDATE_LOGGED_IN,
});
